import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import ContentCard from "@/components/ContentCard";
import MediaSlideshow from "@/components/MediaSlideshow";
import KartaSida from "@/pages/KartaSida";
import { ArrowRight, Camera, MapPin, Activity, Megaphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { OrnamentDivider, MuchaRose } from "@/components/Ornaments";
import BygdepulsBlock from "@/components/BygdepulsBlock";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { t } = useTranslation();
  const location = useLocation();
  const [feed, setFeed] = useState([]);
  // Etappe 4.6 — separater "Kommande"-block borttagen från startsidan.
  // Evenemang nås via huvudmenyn och /evenemang. Bygdepuls fortsätter visa aktuella händelser.
  const [bilder, setBilder] = useState([]);

  usePageMeta({
    title: "vidalv.live – Backe, Rossön och bygderna",
    description: "Lokal samlingsplats för Backe, Rossön och bygden vid Fjällsjöälven. Anslagstavla, evenemang, företag, friluftsliv, lediga hyresrätter och karta – modererad och utan reklam.",
    path: "/",
  });

  useEffect(() => {
    axios.get(`${API}/aktuellt`).then(r => setFeed(r.data)).catch(() => {});
    axios.get(`${API}/bilder`).then(r => setBilder(r.data)).catch(() => {});
  }, []);

  // Smooth-Scroll till t.ex. #bygdepuls när hash i URL:en finns (från header-menyn).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const t1 = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t1);
  }, [location.hash]);

  return (
    <div data-testid="home-page">

      {/* ─────────────────────────────────────────────────────────────── */}
      {/* 1. HERO-DIASHOW                                                  */}
      {/* ─────────────────────────────────────────────────────────────── */}
      <section data-testid="welcome-section" className="relative bg-ink">
        <div
          data-testid="hero-slideshow-band"
          className="relative w-full h-[68vh] min-h-[480px] max-h-[720px]"
        >
          <MediaSlideshow items={bilder} />
        </div>

        {/* Subline + namnet-teaser direkt under bilden */}
        <div className="bg-bone border-b border-warm-border">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 text-center flex flex-col items-center">
            <MuchaRose size={56} className="text-gold-deep opacity-55" />
            <p className="mt-2 font-heading text-base sm:text-lg italic text-bark leading-relaxed">
              {t("home.subline")}
            </p>
            <Link
              to="/upptack/namnet"
              data-testid="banner-namnet-teaser"
              className="inline-flex items-center gap-1.5 mt-3 text-xs sm:text-sm font-body text-bark/60 hover:text-falu no-underline transition-colors"
            >
              {t("home.namnet_teaser")} <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────── */}
      {/* 2. BYGDEPULS (Sprunganker #bygdepuls)                            */}
      {/* ─────────────────────────────────────────────────────────────── */}
      <div id="bygdepuls" data-testid="bygdepuls-anchor" className="scroll-mt-20">
        {/* Build 96.13 — CTA renderas som slot direkt under Bygdepuls-rubriken,
            före notiserna. Ordning: Rubrik → CTA → Innehåll. */}
        <BygdepulsBlock belowRubrikSlot={(
          <div
            data-testid="bygdepuls-cta"
            className="mb-3 bg-parchment border border-warm-border rounded-sm px-4 py-3 sm:px-5 sm:py-4"
          >
            <div className="flex items-start gap-3">
              <Activity size={18} className="shrink-0 text-pine mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3
                  data-testid="bygdepuls-cta-rubrik"
                  className="font-heading text-base sm:text-lg font-semibold text-ink leading-tight"
                >
                  Hej – är något på gång i bygden?
                </h3>
                <p className="mt-1.5 text-sm text-bark/75 leading-relaxed">
                  Har du hört om något som händer, något som växer fram eller något
                  fler borde känna till? Tipsa oss gärna. Små nyheter kan hjälpa
                  andra att känna sig mer hemma.
                </p>
                <Link
                  to="/skicka-anslag?typ=bygdepuls"
                  data-testid="bygdepuls-cta-link"
                  className="inline-flex items-center gap-1.5 mt-2.5 text-sm font-semibold text-falu hover:text-falu-hover no-underline transition-colors"
                >
                  Tipsa om bygdepuls <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        )} />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        {/* ─────────────────────────────────────────────────────────── */}
        {/* 3. KARTA — samma komponent som /karta, inbäddad               */}
        {/* ─────────────────────────────────────────────────────────── */}
        <OrnamentDivider className="text-gold/40 my-4" />

        <section data-testid="karta-section" className="py-4 sm:py-6">
          {/* Build 96.13 — CTA passeras som slot direkt under kart-rubriken,
              före själva kartan. Ordning: Rubrik → CTA → Karta. */}
          <KartaSida embedded belowRubrikSlot={(
            <div
              data-testid="karta-cta"
              className="mb-4 bg-parchment border border-warm-border rounded-sm px-4 py-3 sm:px-5 sm:py-4"
            >
              <div className="flex items-start gap-3">
                <MapPin size={18} className="shrink-0 text-pine mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h3
                    data-testid="karta-cta-rubrik"
                    className="font-heading text-base sm:text-lg font-semibold text-ink leading-tight"
                  >
                    Hej – du vet kanske något vi har missat
                  </h3>
                  <p className="mt-1.5 text-sm text-bark/75 leading-relaxed">
                    Tack för att du tittar noga. Kanske ser du ett fel, en plats som
                    saknas, ett gammalt namn eller en liten detalj som bara någon med
                    lokal kunskap känner till. Skicka gärna ett tips och hjälp oss
                    göra kartan mer levande.
                  </p>
                  <Link
                    to="/skicka-anslag?typ=karta"
                    data-testid="karta-cta-link"
                    className="inline-flex items-center gap-1.5 mt-2.5 text-sm font-semibold text-falu hover:text-falu-hover no-underline transition-colors"
                  >
                    Skicka ett karttips <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          )} />
          <div className="mt-4 text-right">
            <Link
              to="/karta"
              data-testid="link-full-karta"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-falu hover:text-falu-hover no-underline"
            >
              Öppna karta i fullvy <ArrowRight size={14} />
            </Link>
          </div>
        </section>

        {/* ─────────────────────────────────────────────────────────── */}
        {/* 4. ANSLAG / AKTUELLT                                         */}
        {/* ─────────────────────────────────────────────────────────── */}
        <OrnamentDivider className="text-gold/40 my-4" />
        <section data-testid="feed-section" className="py-6 sm:py-8">
          <p className="font-body text-xs font-semibold text-bark/40 uppercase tracking-[0.2em] mb-3">Nytt i bygden</p>

          {/* Build 96.13 — Mitmach-CTA direkt under rubriken, före anslagslistan.
              Ordning: Rubrik → CTA → Innehåll. */}
          <div
            data-testid="anslag-cta"
            className="mb-4 bg-parchment border border-warm-border rounded-sm px-4 py-3 sm:px-5 sm:py-4"
          >
            <div className="flex items-start gap-3">
              <Megaphone size={18} className="shrink-0 text-pine mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3
                  data-testid="anslag-cta-rubrik"
                  className="font-heading text-base sm:text-lg font-semibold text-ink leading-tight"
                >
                  Hej – behöver du hjälp eller vill du hjälpa till?
                </h3>
                <p className="mt-1.5 text-sm text-bark/75 leading-relaxed">
                  Söker du något, erbjuder du något eller vill du bjuda in andra?
                  Ett anslag kan hjälpa människor i bygden att hitta varandra.
                </p>
                <Link
                  to="/skicka-anslag?typ=anslag"
                  data-testid="anslag-cta-link"
                  className="inline-flex items-center gap-1.5 mt-2.5 text-sm font-semibold text-falu hover:text-falu-hover no-underline transition-colors"
                >
                  Skicka anslag <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>

          {/* Build 96.10 — dubblerad Evenemang-visning på startsidan borttagen.
              /api/aktuellt returnerar en blandning av anslag + kommande evenemang, men
              Evenemang har egen ingång i huvudmenyn och egen puls-notis. Här visas
              därför endast anslag för att undvika visuell doppling. Backend, evenemang-
              funktionen och ICS-sync är helt orörda. */}
          <div className="flex flex-col gap-2">
            {feed.filter((item) => item.typ !== "evenemang").slice(0, 3).map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
            <Link to="/anslagstavla" data-testid="link-alla-anslag" className="inline-flex items-center gap-1.5 text-sm font-semibold text-falu hover:text-falu-hover no-underline transition-colors mt-2">
              Alla anslag <ArrowRight size={14} />
            </Link>
          </div>

        </section>

        {/* ─────────────────────────────────────────────────────────── */}
        {/* 5. BILDER FRÅN BYGDEN                                        */}
        {/* ─────────────────────────────────────────────────────────── */}
        <section data-testid="tavling-section" className="py-8 sm:py-10">
          <Link
            to="/bilder"
            data-testid="tavling-rubrik"
            className="block no-underline group"
          >
            <div className="relative bg-falu border border-falu-hover rounded-sm overflow-hidden">
              <div className="absolute top-4 right-4 opacity-10">
                <Camera size={140} className="text-parchment" strokeWidth={1} />
              </div>
              <div className="relative p-6 sm:p-10">
                <div className="flex items-center gap-2 mb-3">
                  <Camera size={16} className="text-parchment/80" />
                  <p className="font-body text-xs font-semibold text-parchment/70 uppercase tracking-[0.25em]">{t("nav.utstallning")}</p>
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-parchment tracking-tight mb-3 max-w-xl">
                  Bilder från bygden
                </h2>
                <p className="text-sm sm:text-base text-parchment/80 leading-relaxed max-w-lg mb-6">
                  En öppen bildsamling från Backe, Rossön och bygderna vid Fjällsjöälven &ndash; foton inskickade av dem som bor här. Alla bidrag visas.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span data-testid="tavling-cta" className="inline-flex items-center gap-1.5 bg-parchment text-falu font-body font-semibold text-sm px-5 py-3 rounded-sm group-hover:bg-parchment/90 transition-colors">
                    Se bildsamlingen och delta <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </div>
          </Link>

          {/* Build 96.13 — Mitmach-CTA direkt under rubriken/Falu-blocket, före galleribilderna.
              Ordning: Rubrik → CTA → Innehåll. */}
          <div
            data-testid="bilder-cta"
            className="mt-4 bg-parchment border border-warm-border rounded-sm px-4 py-3 sm:px-5 sm:py-4"
          >
            <div className="flex items-start gap-3">
              <Camera size={18} className="shrink-0 text-pine mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3
                  data-testid="bilder-cta-rubrik"
                  className="font-heading text-base sm:text-lg font-semibold text-ink leading-tight"
                >
                  Hej – har du en bild som kan hjälpa andra?
                </h3>
                <p className="mt-1.5 text-sm text-bark/75 leading-relaxed">
                  En bild kan visa en plats, en väg, en aktivitet eller en stämning
                  bättre än många ord. Dela gärna en bild som hjälper fler att känna
                  igen bygden.
                </p>
                <Link
                  to="/bilder/skicka"
                  data-testid="bilder-cta-link"
                  className="inline-flex items-center gap-1.5 mt-2.5 text-sm font-semibold text-falu hover:text-falu-hover no-underline transition-colors"
                >
                  Skicka en bild <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>

          {/* Senaste bidrag */}
          {bilder.length > 0 && (
            <div data-testid="bilder-preview" className="mt-4">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {bilder.slice(0, 3).map((b) => {
                  const VIDEO_EXTS_RE = /\.(mp4|mov|webm|m4v|m4a|ogg|ogv)(\?|$)/i;
                  const isVideo = (b.media_typ === "video") || VIDEO_EXTS_RE.test(b.bild_url || "");
                  const src = b.bild_url ? (b.bild_url.startsWith("http") ? b.bild_url : `${process.env.REACT_APP_BACKEND_URL}${b.bild_url}`) : null;
                  return (
                  <Link
                    key={b.id}
                    to="/bilder"
                    data-testid={`bild-preview-${b.id}`}
                    className="block aspect-square bg-parchment border border-warm-border rounded-sm overflow-hidden group/tile relative no-underline"
                  >
                    {src ? (
                      isVideo ? (
                        <>
                          <video
                            src={`${src}#t=0.5`}
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover"
                          />
                          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="w-10 h-10 rounded-full bg-parchment/85 flex items-center justify-center shadow-md">
                              <span className="w-0 h-0 border-l-[10px] border-l-ink border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-0.5" />
                            </span>
                          </span>
                        </>
                      ) : (
                        <img
                          src={src}
                          alt={b.titel || ""}
                          className="w-full h-full object-cover group-hover/tile:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      )
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-bone-alt">
                        <Camera size={24} className="text-bark/30" />
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/70 to-transparent p-2.5">
                      <p className="text-[10px] sm:text-xs font-body font-medium text-parchment truncate">{b.titel}</p>
                      {b.fotograf && <p className="text-[9px] sm:text-[10px] text-parchment/60 truncate">Foto: {b.fotograf}</p>}
                    </div>
                  </Link>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
